import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
  alpha
} from '@mui/material'

import { apiActions } from '../../redux'
import { Section } from './Section'

const ChipListEditor = ({
  title,
  hint,
  items = [],
  options = [],
  placeholder,
  freeSolo = true,
  saving = false,
  onSave
}) => {
  const [local, setLocal] = useState(items)
  const [inputValue, setInputValue] = useState('')
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    setLocal(items)
    setDirty(false)
    setInputValue('')
  }, [items])

  const addValue = (raw) => {
    const value = String(raw || '').trim()
    if (!value) return
    if (local.some((x) => x.toLowerCase() === value.toLowerCase())) {
      setInputValue('')
      return
    }
    setLocal([...local, value])
    setDirty(true)
    setInputValue('')
  }

  const remove = (value) => {
    setLocal(local.filter((x) => x !== value))
    setDirty(true)
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontWeight: 800, fontSize: '0.86rem' }}>{title}</Typography>
      {hint
        ? (
          <Typography variant='caption' sx={{ color: alpha('#fff', 0.65), fontWeight: 600, display: 'block', mb: 1 }}>
            {hint}
          </Typography>
          )
        : <Box sx={{ mb: 1 }} />}
      <Stack spacing={1.25}>
        <Stack direction='row' flexWrap='wrap' gap={0.75}>
          {local.length
            ? local.map((value) => (
              <Chip
                key={value}
                label={value}
                size='small'
                onDelete={saving ? undefined : () => remove(value)}
                sx={{ maxWidth: '100%' }}
              />
            ))
            : (
              <Typography variant='caption' sx={{ color: alpha('#fff', 0.45) }}>
                None linked
              </Typography>
              )}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Autocomplete
            size='small'
            fullWidth
            freeSolo={freeSolo}
            options={options}
            value={null}
            inputValue={inputValue}
            onChange={(_, value) => {
              if (value == null) return
              addValue(value)
            }}
            onInputChange={(_, value) => setInputValue(value)}
            disabled={saving}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={placeholder}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addValue(inputValue)
                  }
                }}
              />
            )}
          />
          <Button
            size='small'
            variant='contained'
            disabled={saving || !dirty}
            onClick={() => onSave?.(local)}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}

const ClientBillingSection = ({
  qboCustomers = [],
  googleProjects = [],
  savingQbo = false,
  savingGoogle = false,
  onSaveQbo,
  onSaveGoogle
}) => {
  const dispatch = useDispatch()
  const qboByCustomer = useSelector((state) => state.api?.qboByCustomer || {})
  const socrClientSettings = useSelector((state) => state.api?.socrClientSettings || {})
  const socrClientTree = useSelector((state) => state.api?.socrClientTree || [])

  useEffect(() => {
    if (!qboByCustomer || Object.keys(qboByCustomer).length === 0) {
      dispatch(apiActions.fetchQbo())
    }
  }, [dispatch, qboByCustomer])

  const qboOptions = useMemo(
    () => Object.keys(qboByCustomer || {}).filter(Boolean).sort((a, b) => a.localeCompare(b)),
    [qboByCustomer]
  )

  const googleOptions = useMemo(() => {
    const set = new Set()
    Object.values(socrClientSettings || {}).forEach((settings) => {
      ;(settings?.googleProjects || []).forEach((id) => {
        if (id) set.add(String(id))
      })
    })
    ;(Array.isArray(socrClientTree) ? socrClientTree : []).forEach((row) => {
      ;(row?.googleProjects || []).forEach((id) => {
        if (id) set.add(String(id))
      })
    })
    return [...set].sort((a, b) => a.localeCompare(b))
  }, [socrClientSettings, socrClientTree])

  return (
    <Section title='Billing & Google' hint='Pick from known inventory or type a new value, then save.'>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2
        }}
      >
        <ChipListEditor
          title='QBO customers'
          hint='Display names used by billing / dashboard joins.'
          items={qboCustomers}
          options={qboOptions}
          placeholder='QBO customer name'
          saving={savingQbo}
          onSave={onSaveQbo}
        />
        <ChipListEditor
          title='Google projects'
          hint='GCP project ids linked to this client.'
          items={googleProjects}
          options={googleOptions}
          placeholder='Google project id'
          saving={savingGoogle}
          onSave={onSaveGoogle}
        />
      </Box>
    </Section>
  )
}

export { ClientBillingSection }
